import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Search, X, ChevronDown } from "lucide-react";
import ProductCard from "../components/ProductCard";
import ProductDetail from "../components/ProductDetail";
import { Reveal } from "../components/Reveal";
import { useQuote } from "../context/QuoteContext";
import { products } from "../data/products";
import { catalogueProducts } from "../data/catalogue";
import Seo from "../components/Seo";

// Combined catalogue: Phase-1 products (with imagery + 360) first, then full range.
const ALL = [...products, ...catalogueProducts];

// Ordered category list built from the data itself.
const CAT_ORDER = [
  "masks", "gloves", "head-covers", "shoe-covers", "protective-wear",
  "hospital-beds", "icu-ward", "nicu", "ot-equipment", "stretchers",
  "trolleys", "furniture", "diagnostic", "imaging", "respiratory",
  "endo-lap", "lab", "surgical-instruments", "dental", "sterilization", "other",
];
const catName = {};
ALL.forEach((p) => { if (!catName[p.category]) catName[p.category] = p.categoryName; });
const CATEGORIES = [{ id: "all", name: "All Products" }].concat(
  CAT_ORDER.filter((id) => catName[id]).map((id) => ({ id, name: catName[id] }))
);

const productsJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Clinixport Global Products",
  itemListElement: ALL.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Product",
      name: p.name,
      sku: p.model || undefined,
      category: p.categoryName,
      ...(p.img ? { image: p.img } : {}),
      description: p.spec || undefined,
      brand: { "@type": "Brand", name: "Clinixport Global" },
    },
  })),
};

export default function Products() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("catalogue");
  const [detail, setDetail] = useState(null);
  const { openQuote } = useQuote();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat && CATEGORIES.some((c) => c.id === cat)) setActive(cat);
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ALL.filter((p) => {
      if (active !== "all" && p.category !== active) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.model || "").toLowerCase().includes(q) ||
        p.categoryName.toLowerCase().includes(q) ||
        (p.spec || "").toLowerCase().includes(q)
      );
    });
    if (sort === "az") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "za") list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    return list;
  }, [active, query, sort]);

  return (
    <div data-testid="products-page" className="pt-32">
      <Seo
        title="Products | Medical Equipment, Hospital Furniture & PPE — Clinixport Global"
        description="Browse Clinixport Global's full catalogue — hospital beds, ICU & OT equipment, stretchers, trolleys, hospital furniture, diagnostic & imaging systems, laboratory equipment, surgical instruments and protective PPE. No prices — request a quote."
        path="/products"
        keywords="medical equipment supplier, hospital beds, ICU bed, OT table, OT light, hospital furniture, patient monitor, autoclave, surgical instruments, nitrile gloves, face masks, PPE supplier"
        jsonLd={productsJsonLd}
      />
      {/* HEADER */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-12">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-5">Product Catalogue</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.02] max-w-4xl">
            One catalogue for the whole hospital.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-7 text-lg text-[#475569] leading-relaxed max-w-2xl">
            From protective essentials to medical, surgical and hospital equipment. No prices, no checkout —
            search the range and request a quote for anything you need.
          </p>
        </Reveal>
      </section>

      {/* CONTROLS */}
      <section className="border-t border-[#0A1128]/10 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="sticky top-[72px] bg-white/95 backdrop-blur z-30 py-5 border-b border-[#0A1128]/10 space-y-4">
            {/* Search + Sort */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="product-search"
                  placeholder="Search products, models or categories…"
                  className="w-full h-12 pl-11 pr-10 bg-[#F1F5F9] border border-transparent focus:border-[#1F6FEB] focus:bg-white outline-none text-[#0A1128] transition-colors"
                />
                {query && (
                  <button onClick={() => setQuery("")} data-testid="search-clear" aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0A1128]">
                    <X size={18} />
                  </button>
                )}
              </div>
              <div className="relative sm:w-56">
                <select
                  value={sort} onChange={(e) => setSort(e.target.value)}
                  data-testid="product-sort" aria-label="Sort products"
                  className="w-full h-12 pl-4 pr-10 bg-[#F1F5F9] border border-transparent focus:border-[#1F6FEB] outline-none text-[#0A1128] font-semibold appearance-none cursor-pointer"
                >
                  <option value="catalogue">Sort: Catalogue order</option>
                  <option value="az">Sort: Name A–Z</option>
                  <option value="za">Sort: Name Z–A</option>
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" />
              </div>
            </div>
            {/* Category filters */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mb-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`filter-${c.id}`}
                  className={`shrink-0 px-4 py-2 text-sm font-bold tracking-wide whitespace-nowrap transition-colors ${
                    active === c.id ? "bg-[#0A1128] text-white" : "text-[#0A1128] hover:bg-[#0A1128]/5 border border-[#0A1128]/10"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-8 pb-6">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#64748B]">
              {CATEGORIES.find((c) => c.id === active)?.name}
            </p>
            <p className="text-xs font-semibold text-[#94A3B8]" data-testid="products-count">
              {filtered.length} {filtered.length === 1 ? "product" : "products"}
            </p>
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center" data-testid="no-results">
              <p className="text-2xl font-bold tracking-tight text-[#0A1128]">No products match your search.</p>
              <p className="mt-3 text-[#475569]">Try a different term, or request a quote for a specific requirement.</p>
              <button onClick={() => openQuote()} className="mt-7 inline-flex bg-[#1F6FEB] hover:bg-[#1556B8] transition-colors text-white px-8 py-3.5 font-bold text-xs tracking-widest uppercase">
                Request a Quote
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} onView={setDetail} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-xl leading-tight">
              Need a product not listed yet?
            </h2>
            <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
              Our wider catalogue and turnkey hospital solutions extend well beyond this list. Tell us what you need.
            </p>
          </div>
          <button onClick={() => openQuote()} data-testid="products-cta-quote"
            className="shrink-0 bg-[#1F6FEB] hover:bg-[#1556B8] transition-colors text-white px-9 py-4 font-bold text-sm tracking-widest uppercase">
            Request a Quote
          </button>
        </div>
      </section>

      <ProductDetail product={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
